// ============================================================
// esign.types.ts — B4c e-signature pilot (batch 1a): the firm's settings
// (Admin → E-signature), the PUBLIC verify results and the signer-link
// lookup. Shapes mirror backend controllers/esignController.ts and
// esignPublicController.ts exactly.
// ============================================================

export interface EsignPlaceholder { key: string; label: string }

export interface EsignConsent {
  // The firm's own wording (null = Casevo's default).
  wording: string | null
  defaultWording: string
  placeholders: EsignPlaceholder[]
  // The saved wording filled with the firm's details.
  preview: string
  // Sent back on Approve — must match what the solicitor read.
  version: string
  approved: boolean
  approvedAt: string | null
  approvedBy: string | null
}

export type EsignSealStatus =
  | { configured: true; subject: string; fingerprint: string; validUntil: string }
  | { configured: false }

export interface EsignEvidenceStatus {
  backupConfigured: boolean
  pendingBackups: number
  lastBackupAt: string | null
  failedChecks: number
  lastCheckAt: string | null
}

// (batch 1b) "How Casevo e-signatures work" — DRAFT until approved for THIS version.
export interface EsignExplainer {
  title: string
  version: string
  sections: { heading: string; paragraphs: string[] }[]
  approved: boolean
  approvedAt: string | null
  approvedBy: string | null
  // An older text was approved — this one needs approving again.
  outdated: boolean
}

export interface EsignSettings {
  pilotEnabled: boolean
  linkDays: number
  retentionYears: number
  // (batch 1b) the client's birthday kept to, if a child when signing
  retentionChildAge: number
  retentionRule: string
  retentionNote: string
  // (batch 1b) the text sender name (null = from the firm name) and what's used
  smsSenderName: string | null
  smsSenderShown: string
  remindersEnabled: boolean
  // (E-sign audit fixes A) the most signing-code texts the firm sends in any 24 hours
  smsDailyCap: number
  explainer: EsignExplainer
  consent: EsignConsent
  status: { seal: EsignSealStatus; evidence: EsignEvidenceStatus; sms: { configured: boolean; provider: string } }
  // (E-sign fixes B) The locked evidence backup job: its last run and what is still waiting.
  backup?: EsignBackupStatus
  // (D12) The retention purge (backend lib/esign/purge purgeStatus; null = couldn't be read).
  purge?: EsignPurgeStatus | null
}
// (D12) GET /api/esign/settings data.purge — is the database guard in, how
// many records are past their keep-until date, how many wait for the S3 lock.
export interface EsignPurgeRun { startedAt: string; finishedAt: string | null; purged: number; failed: number; remaining?: number; error: string | null }
export interface EsignPurgeStatus { guardInstalled: boolean; due: number; waitingForLock: number; lastRun: EsignPurgeRun | null }
// (E-sign fixes B) GET /api/esign/settings data.backup.
export interface EsignBackupRun { startedAt: string; finishedAt: string | null; copied: number; failed: number; remaining: number; error: string | null }
export interface EsignBackupStatus { configured: boolean; lastRun: EsignBackupRun | null; backlog: number }

export interface EsignSettingsPatch {
  pilotEnabled?: boolean
  linkDays?: number
  retentionYears?: number
  retentionChildAge?: number
  remindersEnabled?: boolean
  smsDailyCap?: number
  // null / '' = a short form of the firm's name.
  smsSenderName?: string | null
  // null = back to Casevo's default. Any change resets the approval.
  consentWording?: string | null
}

// (E-sign audit fixes A) A PATCH that switched the pilot OFF cancels every open link.
export type EsignSettingsSaved = EsignSettings & { linksRevoked?: number; revokeFailed?: number }

// (D12) Ask a colleague to confirm — the refusal reason's limit (server-enforced).
export const SECOND_CHECK_REFUSE_MAX = 500

// Range limits the server enforces (esignController.patchEsignSettings).
export const ESIGN_LINK_DAYS = { min: 3, max: 30 } as const
export const ESIGN_RETENTION_YEARS = { min: 6, max: 50 } as const
export const ESIGN_CONSENT_LENGTH = { min: 40, max: 2000 } as const
export const ESIGN_CHILD_AGE = { min: 18, max: 30 } as const
export const ESIGN_SMS_DAILY_CAP = { min: 10, max: 5000 } as const
// A UK alphanumeric text sender: 1–11 letters / digits (spaces allowed), at least one letter.
export const SMS_SENDER_RE = /^(?=.*[A-Za-z])[A-Za-z0-9 ]{1,11}$/
// (E-sign fixes B) The rule now: 1–11 letters or numbers, NO spaces, at least one letter (the server’s check).
export const SMS_SENDER_NAME_RE = /^(?=.*[A-Za-z])[A-Za-z0-9]{1,11}$/

// ── Public verify ────────────────────────────────────────────
export interface EsignPublicSigner { name: string; signedAt: string | null }
// What a stranger may see about a sealed record.
export interface EsignPublicRecord {
  firmName: string
  reference: string
  documentType: string
  status: string
  sealedAt: string | null
  timestampBy: string | null
  signers: EsignPublicSigner[]
}
export type VerifyByRefResult = EsignPublicRecord & { result: 'FOUND' }
export type VerifyUploadResult =
  | (EsignPublicRecord & { result: 'UNCHANGED'; fileSha256: string; isCertificate: boolean; timestampAt: string | null })
  | { result: 'MODIFIED'; fileSha256: string; reason: string }
  | { result: 'NOT_OURS'; fileSha256: string; reason: string }
export type VerifyResult = VerifyByRefResult | VerifyUploadResult

// (E-sign audit fixes A) The seal certificate, published (GET /api/verify/seal).
export type SealCertificateInfo =
  | { configured: false }
  | {
    configured: true
    subject: string
    // SHA-256 of the certificate: hex, and "AB:CD:…" (what Acrobat shows)
    fingerprint: string
    fingerprintColons: string
    validFrom: string
    validTo: string
    expired: boolean
    // Earlier seal certificates still found on sealed documents (hex).
    previous: string[]
  }

// The upload limit the server accepts (express.raw limit '25mb').
export const VERIFY_MAX_BYTES = 25 * 1024 * 1024
// A verify reference (the QR / short link on a certificate).
export const VERIFY_REF_RE = /^[A-Z2-9]{12}$/

// ── (batch 1b) The signing page (/s/<token>) ─────────────────
// backend lib/esign/signing publicView. Nothing about the document's
// content before the code; `session` only once the code is right.
export type SignerLinkState = 'OK' | 'EXPIRED' | 'REVOKED' | 'CLOSED' | 'SIGNED' | 'DECLINED' | 'LOCKED'
export interface SignerFirm { name: string; phone: string | null; email: string | null; logoDataUri: string | null; accent: string }
export interface SignerSession {
  consent: { text: string; version: string } | null
  consentGiven: boolean
  viewedToEnd: boolean
  document: { id: string; title: string; pageCount: number; sha256: string; sizeBytes: number } | null
  settlement: {
    number: string | null; version: number; totalDeductionsPence: number; courtApproves: boolean
    // (E-sign fixes B) the money, for the answer step (Damages · Deductions · You receive)
    figures?: SignFigure[]
  } | null
  expiresInSeconds: number
  // (E-sign fixes B) how the signer showed they read it (null = not yet)
  readMethod?: SignReadMethod | null
}
// (E-sign fixes B) The public signing page's extra shapes.
export type SignReadMethod = 'SCROLL' | 'BUTTON' | 'DOWNLOAD'
export type SignDeclineKind = 'NOT_ME' | 'PAPER' | 'DECLINE'
export interface SignFigure { key: 'damages' | 'deductions' | 'receive'; label: string; pence: number }
// POST sign answers AT ONCE; the sealed copy follows (poll the view for sealed).
export interface SignResult { signed?: true; sealed: boolean; sealing?: boolean; answerOutcome?: string | null; replayed?: boolean }
export interface SignSessionRefresh { session: string; expiresInSeconds: number }
export interface SignerLinkLookup {
  state: SignerLinkState
  firm: SignerFirm
  sender: { name: string }
  signer: { name: string; role: 'CLIENT' | 'LITIGATION_FRIEND' | 'THIRD_PARTY'; onBehalfOf: string | null; maskedMobile: string | null }
  document: { title: string; kind: string; kindLabel: string; reference: string }
  lockedUntil: string | null
  // (E-sign audit fixes A) stopped after too many wrong codes until the firm sends a new link
  hardLocked?: boolean
  revokeReason: string | null
  signedAt: string | null
  verified: boolean
  session?: SignerSession
  // (E-sign fixes B) state SIGNED: the sealed copy can be downloaded
  sealed?: boolean
  // (E-sign fixes B) state DECLINED: why (after "Not you?" the page must not say "you chose not to sign")
  declineKind?: SignDeclineKind | null
}
export interface SignBody {
  // (E-sign audit fixes A) one per Sign press; a retry of the same press reuses it.
  idempotencyKey: string
  documentSha256: string
  answer?: 'AGREED' | 'DISPUTED'
  disputedPence?: number | null
  disputeReason?: string
  signatureType: 'TYPED' | 'DRAWN'
  typedName: string
  drawnPng?: string
  // (E-sign fixes B) the signer confirmed a typed name that doesn't match the record
  nameMismatchConfirmed?: boolean
}

// ── (batch 1b) Staff: e-signature on a settlement statement ──
export type EsignFixTarget = 'esignSettings' | 'clientDetails' | 'litigationFriend' | 'emailConsent'
export interface EsignReadinessItem { key: string; done: boolean; message: string; hint?: string; fix?: { label: string; target: EsignFixTarget } }
// (E-sign audit fixes A) A recent change to the signer's mobile / email
// (masked values) — a change in the last `windowDays` needs a colleague to
// confirm the details before the link can be sent.
export interface EsignContactChange { at: string; byName: string; from: string | null; to: string | null }
export interface EsignContactCheck {
  windowDays: number
  mobile: EsignContactChange | null
  email: EsignContactChange | null
  recent: (EsignContactChange & { channel: 'MOBILE' | 'EMAIL' })[]
  needsSecondCheck: boolean
  singleUserFirm: boolean
  approval: null | {
    id: string; status: 'PENDING' | 'APPROVED' | 'REFUSED'; requestedBy: string; approvedBy: string | null; approvedAt: string | null; createdAt: string
    // (D12) A colleague refused (and why); who was asked (bell + email).
    refusedBy?: string | null; refusedAt?: string | null; refuseReason?: string | null; askedNames?: string[]
  }
  canApprove: boolean
  // (E-sign fixes B) Colleagues who CAN confirm (active, not you, can open
  // this statement and the case) — and the other active colleagues who can't,
  // with why ("needs “See colleagues’ billing”", "no access to this case").
  // `singleUserFirm` is true only when nobody else at the firm is active.
  confirmers?: string[]
  others?: { name: string; reason: string }[]
}
// (E-sign fixes B) How the signer confirmed reading the document.
export type EsignReadMethod = 'SCROLL' | 'BUTTON' | 'DOWNLOAD'
export type EsignDisplayStatus = 'SENT' | 'OPENED' | 'CODE_VERIFIED' | 'SIGNED' | 'DECLINED' | 'EXPIRED' | 'CANCELLED'
export interface StatementEsignSummary {
  id: string
  reference: string
  status: EsignDisplayStatus
  requestStatus: string
  sealStatus: string
  sentAt: string | null
  sentVia: 'OUTLOOK' | 'SYSTEM' | null
  completedAt: string | null
  revokedAt: string | null
  revokeReason: string | null
  answerRecorded: boolean
  keepUntil: string | null
  verifyUrl: string | null
  signer: {
    name: string; role: string; onBehalfOf: string | null; openedAt: string | null; verifiedAt: string | null; signedAt: string | null
    declinedAt: string | null; declineReason: string | null; answer: 'AGREED' | 'DISPUTED' | null; disputedPence: number | null; disputeReason: string | null
    linkExpiresAt: string | null; signatureType: 'TYPED' | 'DRAWN' | null
    // (E-sign fixes B) Signed with a name that doesn't match the record (the
    // signer confirmed it) — shown to the fee earner.
    nameMismatch?: { typed: string; onRecord: string } | null
    readMethod?: EsignReadMethod | null
    // (D12) Why it was declined: "Not me", "I'd rather sign on paper", or a plain decline.
    declineKind?: SignDeclineKind | null
  } | null
  signedPdfId: string | null
  certificateId: string | null
  // (E-sign fixes B) Signed, but the sealed copy isn't ready yet ("Signed — sealing…").
  sealing?: boolean
}
export type StatementEsign =
  | { available: false }
  | {
    available: true
    ready: boolean
    items: EsignReadinessItem[]
    signer: { name: string; role: string; onBehalfOf: string | null; email: string | null; maskedMobile: string | null } | null
    sendableError: string | null
    current: StatementEsignSummary | null
    // (E-sign audit fixes A) the second staff check (null = no signer yet)
    contactCheck?: EsignContactCheck | null
  }

// ── Staff: signing requests (API only in 1a; UI in a later batch) ──
export interface SigningRequestSummary {
  id: string
  caseId: string | null
  reference: string
  kind: string
  title: string
  status: string
  sealStatus: string
  createdAt: string
  sentAt: string | null
  completedAt: string | null
  signers: { name: string; role: string; status: string }[]
}
export interface SigningRequestDetail {
  id: string
  reference: string
  verifyRef: string
  kind: string
  title: string
  status: string
  caseId: string | null
  sealStatus: string
  sealedAt: string | null
  timestampAt: string | null
  tsaName: string | null
  finalSha256: string | null
  keepUntil: string | null
  createdAt: string
  sentAt: string | null
  completedAt: string | null
  revokedAt: string | null
  revokeReason: string | null
  documents: { id: string; title: string; pageCount: number; originalSha256: string; status: string; required: boolean }[]
  signers: { id: string; name: string; role: string; onBehalfOf: string | null; email: string | null; mobile: string | null; status: string; signedAt: string | null; declinedAt: string | null; tokenExpiresAt: string | null }[]
  evidence: { id: string; kind: string; sha256: string; sizeBytes: number; backedUpAt: string | null; lastCheckOk: boolean | null }[]
  events: { seq: number; type: string; at: string; signerId: string | null; ip: string | null; hash: string }[]
  chain: unknown
}
